import { captureException, startSpan } from "@sentry/react";

import {
    IImagesResponse,
    ISessionCostResponse,
    ISessionResponse,
    ISessionsResponse,
} from "../../types";
import { doGet, doGetOptAuth, doPost } from "../core/http";

export const streamAudioData = async (
    audioBlob: Blob,
    transcriptionName: string
): Promise<ISessionCostResponse> => {
    const formData = new FormData();
    formData.append("audio", audioBlob, "audio.webm");
    formData.append("name", transcriptionName);
    try {
        const response = await startSpan(
            { name: "streamAudioData" },
            async () => {
                return await doPost("/api/session/stream", true, formData);
            }
        );

        if (response.error === "Payment Required") {
            return { error: "Payment Required" };
        }

        if (response.error) {
            if (response.error.status === 404) {
                return { error: response.error }; // Return error object
            } else {
                captureException(response.error);
                return { error: response.error }; // Return error object
            }
        }
        return response.data;
    } catch (e) {
        captureException(e);
        return {
            error: "An error occurred while streaming audio data.",
        } as ISessionCostResponse;
    }
};

export const getSessionImages = async (
    transcriptionName: string
): Promise<IImagesResponse> => {
    try {
        const response = await startSpan(
            { name: "getSessionImages" },
            async () => {
                return await doGet(
                    `/api/session/${transcriptionName}/images`,
                    true
                );
            }
        );

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return {
            image: response.data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get images" };
    }
};

export const getSessionImagesById = async (
    sessionId: string
): Promise<IImagesResponse> => {
    try {
        const response = await startSpan(
            { name: "getSessionImagesById" },
            async () => {
                return await doGetOptAuth(
                    `/api/session/id/${sessionId}/images`
                );
            }
        );

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return {
            image: response.data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get images" };
    }
};

export const getSessions = async (): Promise<ISessionsResponse> => {
    try {
        const response = await startSpan({ name: "getSessions" }, async () => {
            return await doGet("/api/session/sessions", true);
        });

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return {
            sessionNames: response.data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get sessions" };
    }
};

export const getSession = async (
    sessionId: string
): Promise<ISessionResponse> => {
    try {
        const response = await startSpan({ name: "getSession" }, async () => {
            return await doGetOptAuth(`/api/session/${sessionId}`);
        });

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return {
            session: response.data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get session" };
    }
};

export const getSessionByName = async (
    sessionName: string
): Promise<ISessionResponse> => {
    try {
        const response = await startSpan(
            { name: "getSessionByName" },
            async () => {
                return await doGet(`/api/session/name/${sessionName}`, true);
            }
        );

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return {
            session: response.data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get session" };
    }
};

export const createSession = async (
    sessionName: string
): Promise<ISessionResponse> => {
    try {
        const formData = new FormData();
        formData.append("sessionName", sessionName);
        const response = await startSpan(
            { name: "createSession" },
            async () => {
                return await doPost("/api/session", true, formData);
            }
        );

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return {
            session: response.data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to create session" };
    }
};
